import React from "react";
import { IoMdClose } from "react-icons/io";
import moment from "moment";
import Avatar from "react-avatar";

const TicketRevenueModal = ({ revenueData, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-2 sm:p-4">
      <div className="max-h-[90vh] w-[95%] sm:w-[700px] overflow-auto rounded-xl bg-navy-800 px-3 pb-3 sm:px-6 sm:pb-6 shadow-lg">

        {/* Header / Banner Section */}
        <div className="flex justify-end py-2 sm:py-3 sticky top-0 bg-navy-800 z-10">
          <IoMdClose
            className="cursor-pointer text-xl sm:text-2xl text-white"
            onClick={onClose}
          />
        </div>

        <div
          className="relative mt-1 flex h-[120px] sm:h-[200px] w-full justify-center rounded-xl bg-cover bg-center"
          style={{
            backgroundImage: revenueData?.eventImage
              ? `url(${revenueData.eventImage.includes("http") ? revenueData.eventImage : process.env.REACT_APP_BASE_URL + revenueData.eventImage})`
              : "linear-gradient(to right, #111c44, #1a224c)"
          }}
        >
          <div className="absolute -bottom-12  sm:-bottom-16 left-4 sm:left-10 flex h-[70px] w-[70px] sm:h-[100px] sm:w-[100px] items-center justify-center rounded-full border-[3px] sm:border-[4px] !border-navy-700 bg-navy-800 shadow-xl overflow-hidden">
            {revenueData?.user?.profileImage ? (
              <img
                src={
                  revenueData.user.profileImage?.includes("http")
                    ? revenueData.user.profileImage
                    : `${process.env.REACT_APP_BASE_URL}${revenueData.user.profileImage}`
                }
                alt="User"
                className="h-full w-full rounded-full object-cover"
              />
            ) : (
              <Avatar
                name={revenueData?.user?.name || "User"}
                className="h-full w-full rounded-full"
                size="100"
              />
            )}
          </div>

          <div className="absolute -bottom-[36px] sm:-bottom-[54px] left-24 sm:left-40 flex flex-col">
            <h4 className="text-sm sm:text-xl font-bold capitalize text-white">
              {revenueData?.user?.name || "--"}{" "}
              <span className="capitalize">({revenueData?.user?.account_type || "--"})</span>
            </h4>
            {revenueData?.user?.email && (
              <p className="text-xs text-gray-600 sm:text-sm">
                {revenueData.user.email}
              </p>
            )}
          </div>
        </div>

        <div className="mt-16 sm:mt-20"></div>

        {/* ==================== 2️⃣ EVENT DETAILS SECTION ==================== */}
        <div className="mb-4">
          <p className="mb-3 text-sm sm:text-lg font-bold uppercase tracking-wider text-white">
            Event Details
          </p>

          <div className="grid grid-cols-6 gap-y-2 sm:gap-y-2">
            {/* Row 1: Title & Description */}
            <p className="col-span-1 text-xs font-normal text-gray-600 sm:text-base">
              Title:
            </p>
            <p className="col-span-2 ml-2 sm:ml-0 break-all text-xs font-normal text-white sm:text-base">
              {revenueData?.title ?? "--"}
            </p>

            <p className="col-span-1 text-xs font-normal text-gray-600 sm:text-base">
              Description:
            </p>
            <p className="col-span-2 break-all ml-4 sm:ml-0 text-xs font-normal text-white sm:text-base">
              {revenueData?.description ?? "--"}
            </p>

            {/* Row 2: Start Date & End Date */}
            <p className="col-span-1 text-xs font-normal text-gray-600 sm:text-base sm:whitespace-nowrap">
              Start Date:
            </p>
            <p className="col-span-2 break-all ml-2 sm:ml-0 text-xs font-normal text-white sm:text-base ">
              {revenueData?.startDate
                ? moment.utc(revenueData.startDate).format("DD/MM/YYYY hh:mm A")
                : "--"}
            </p>

            <p className="col-span-1 text-xs font-normal text-gray-600 sm:text-base sm:whitespace-nowrap">
              End Date:
            </p>
            <p className="col-span-2 ml-2 sm:ml-0 break-all text-xs font-normal text-white sm:text-base ">
              {revenueData?.endDate
                ? moment.utc(revenueData.endDate).format("DD/MM/YYYY hh:mm A")
                : "--"}
            </p>

            {/* Row 3: Latitude & Longitude */}
            <p className="col-span-1  text-xs font-normal text-gray-600 sm:text-base">
              Latitude:
            </p>
            <p className="col-span-2 ml-2 sm:ml-0 break-all text-xs font-normal text-white sm:text-base">
              {revenueData?.event?.location?.coordinates
                ? revenueData.event.location.coordinates[1]
                : "--"}
            </p>

            <p className="col-span-1 text-xs font-normal text-gray-600 sm:text-base">
              Longitude:
            </p>
            <p className="col-span-2 ml-3 sm:ml-0 break-all text-xs font-normal text-white sm:text-base">
              {revenueData?.event?.location?.coordinates
                ? revenueData.event.location.coordinates[0]
                : "--"}
            </p>
          </div>
        </div>

        {/* ==================== 3️⃣ TICKET DETAILS SECTION ==================== */}
        <div className="mb-6">
          <p className="mb-3 text-sm sm:text-lg font-bold uppercase tracking-wider text-white">
            Ticket Details
          </p>

          <div className="grid grid-cols-6 gap-y-2 sm:gap-y-2">
            {/* Row 1: Title & Description */}
            <p className="col-span-1 text-xs font-normal text-gray-600 sm:text-base">
              Title:
            </p>
            <p className="col-span-2 break-all text-xs font-normal text-white sm:text-base">
              {revenueData?.ticketName ?? "--"}
            </p>

            <p className="col-span-1 text-xs font-normal text-gray-600 sm:text-base">
              Description:
            </p>
            <p className="col-span-2 ml-5 sm:ml-0 break-all text-xs font-normal text-white sm:text-base">
              {revenueData?.ticketDescription ?? "--"}
            </p>

            {/* Row 2: Price & Buy Ticket */}
            <p className="col-span-1 text-xs font-normal text-gray-600 sm:text-base">
              Price:
            </p>
            <p className="col-span-2 break-all text-xs font-normal text-white sm:text-base">
              ${revenueData?.ticketPrice ?? "--"}
            </p>

            <p className="col-span-1 text-xs font-normal whitespace-nowrap text-gray-600 sm:text-base">
              Buy Ticket:
            </p>
            <p className="col-span-2 break-all text-xs ml-5 sm:ml-0 font-normal text-white sm:text-base">
              {revenueData?.ticketBuyCount ?? "--"}
            </p>

            {/* Row 3: Total Amount & Service Fee */}
            <p className="col-span-1 text-xs whitespace-nowrap font-normal text-gray-600 sm:text-base">
              Total Amount:
            </p>
            <p className="col-span-2 break-all ml-5 sm:ml-0 text-xs font-normal text-white sm:text-base">
              ${((Number(revenueData?.amount) || 0) + (Number(revenueData?.serviceFee) || 0))}
            </p>

            {/* <p className="col-span-1 text-xs whitespace-nowrap font-normal text-gray-600 sm:text-base">
              Service Fee:
            </p>
            <p className="col-span-2 break-all ml-5 sm:ml-0 text-xs font-normal text-white sm:text-base">
              ${(revenueData?.serviceFee !== undefined && revenueData?.serviceFee !== null) ? revenueData.serviceFee : "0"}
            </p> */}
          </div>

          {/* Ticket Breakdown Cards */}
          {revenueData?.ticketOptions?.length > 0 && (
            <div className="mt-8">
              <p className="mb-4 text-xs font-bold uppercase tracking-wider text-orangePrimary sm:text-sm">
                Ticket Breakdown
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {revenueData.ticketOptions.map((ticket, index) => (
                  <div
                    key={index}
                    className="rounded-xl border border-white/5 bg-navy-700/40 p-4 shadow-sm"
                  >
                    <p className="mb-3 text-sm font-bold text-white sm:text-base">
                      {ticket.title}
                    </p>
                    <div className="grid grid-cols-2 gap-y-2 text-xs sm:text-sm">
                      <span className="text-gray-400">Price:</span>
                      <span className="text-white text-right">₹{ticket.price ?? 0}</span>

                      <span className="text-gray-400">Total:</span>
                      <span className="text-white text-right">{ticket.total}</span>

                      <span className="text-gray-400">Sold:</span>
                      <span className="text-white text-right">{ticket.sold}</span>

                      <span className="text-gray-400">Scanned Quantity:</span>
                      <span className="text-white text-right">
                        {ticket.scanned ?? 0}
                      </span>

                      <span className="mt-1 border-t border-white/10 pt-2 font-semibold text-gray-400">
                        Revenue:
                      </span>
                      <span className="mt-1 border-t border-white/10 pt-2 text-right font-bold text-orangePrimary">
                        ₹{ticket.revenue}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketRevenueModal;
